import { Sun, Droplets, Sprout, Flower, Calendar, Shovel, Lightbulb, BookmarkPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export interface PlantInfo {
  popular_name: string;
  scientific_name: string;
  planting_season: string;
  soil_type: string;
  sunlight_needs: string;
  water_needs: string;
  cultivation_tips: string;
}

interface PlantResultProps {
  plant: PlantInfo;
  imageUrl: string;
  onSave: () => void;
  saving?: boolean;
  saved?: boolean;
}

export function PlantResult({ plant, imageUrl, onSave, saving, saved }: PlantResultProps) {
  const infoItems = [
    {
      icon: Calendar,
      label: 'Época de plantio',
      value: plant.planting_season,
      color: 'text-sun',
    },
    {
      icon: Shovel,
      label: 'Tipo de solo',
      value: plant.soil_type,
      color: 'text-earth',
    },
    {
      icon: Sun,
      label: 'Luz solar',
      value: plant.sunlight_needs,
      color: 'text-sun',
    },
    {
      icon: Droplets,
      label: 'Rega',
      value: plant.water_needs,
      color: 'text-water',
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with image and names */}
      <Card className="overflow-hidden glass-card">
        <div className="relative h-48 overflow-hidden">
          <img
            src={imageUrl}
            alt={plant.popular_name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-2xl font-bold text-foreground font-display">
              {plant.popular_name}
            </h2>
            <p className="text-sm text-muted-foreground italic">
              {plant.scientific_name}
            </p>
          </div>
        </div>
      </Card>

      {/* Info cards */}
      <div className="grid grid-cols-2 gap-3">
        {infoItems.map((item, index) => (
          <Card key={index} className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <item.icon className={`h-4 w-4 ${item.color}`} />
                <span className="text-xs font-medium text-muted-foreground">
                  {item.label}
                </span>
              </div>
              <p className="text-sm font-medium text-foreground">{item.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Cultivation tips */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lightbulb className="h-5 w-5 text-sun" />
            Dicas de cultivo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
            {plant.cultivation_tips}
          </p>
        </CardContent>
      </Card>

      {/* Save button */}
      <Button
        onClick={onSave}
        disabled={saving || saved}
        className="w-full gradient-nature text-primary-foreground"
        size="lg"
      >
        {saving ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Salvando...
          </>
        ) : saved ? (
          <>
            <Sprout className="mr-2 h-5 w-5" />
            Salvo na coleção!
          </>
        ) : (
          <>
            <BookmarkPlus className="mr-2 h-5 w-5" />
            Salvar na coleção
          </>
        )}
      </Button>
    </div>
  );
}
